import { IMAGE_REMOTE_HOSTS } from "@/lib/images/remote-hosts";

/** Widths the Next.js image resizer accepts (its default device and image sizes). */
const WIDTHS = [
	16, 32, 48, 64, 96, 128, 256, 384, 640, 750, 828, 1080, 1200, 1920, 2048,
	3840,
];

/** Hosts that serve SVG (generated avatars, placeholders): not resizable. */
const SVG_HOSTS = new Set(["api.dicebear.com", "placehold.co"]);
const HOSTS = new Set<string>(
	IMAGE_REMOTE_HOSTS.filter((host) => !SVG_HOSTS.has(host)),
);

function fit(width: number) {
	return WIDTHS.find((candidate) => candidate >= width) ?? 3840;
}

function canResize(src: string) {
	const path = src.split("?")[0]?.toLowerCase() ?? "";
	// SVGs scale already; GIFs may be animated.
	if (path.endsWith(".svg") || path.endsWith(".gif")) return false;
	if (src.startsWith("/") && !src.startsWith("//")) {
		return !src.startsWith("/_next/") && !src.includes("?");
	}
	try {
		const url = new URL(src);
		return url.protocol === "https:" && HOSTS.has(url.hostname);
	} catch {
		return false;
	}
}

function resized(src: string, width: number) {
	return `/_next/image?url=${encodeURIComponent(src)}&w=${width}&q=75`;
}

/**
 * `src` and `srcSet` for a plain <img> shown about `width` CSS px wide:
 * the image is resized (and served as WebP/AVIF) by Next.js, with a 2x
 * version for sharp screens. Anything that can't be resized (other hosts,
 * blob: previews, SVG, GIF) passes through unchanged.
 */
export function edImage(
	src: string | null | undefined,
	width: number,
): { src: string; srcSet?: string } {
	if (!src) return { src: "" };
	if (!canResize(src)) return { src };
	const one = fit(width);
	const two = fit(width * 2);
	return {
		src: resized(src, one),
		srcSet:
			one === two
				? undefined
				: `${resized(src, one)} 1x, ${resized(src, two)} 2x`,
	};
}
